import { useCallback } from 'react';
import clsx from 'clsx';
import {
  Page,
  PageBody,
  PageHeader,
  PageHeaderTitle,
  PageHeaderDescription,
  Grid,
  Flex,
  Box,
  Avatar,
  Button,
  Icon,
  Link,
  PlainLink,
  Text,
} from '@customerio/pluma-components/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { PageTitle } from '@/components/page-title';
import { generatePath } from '@/services/router/generate-path';
import { useAnalytics } from '@/services/analytics';
import { useWorkspaceIdFromCurrentUrl } from '@/utils/workspace-id';
import { CopilotInput } from '@/features/copilot';
import { open as openSidebar, useSidebarActive } from '@/features/sidebar';
import { ActivityChart } from './components/activity-chart';
import { useGreeting } from './components/greeting';
import { LegacyDashboard } from './components/legacy-dashboard';
import { MessageInvadersCard } from './components/message-invaders';
import { PromptCards } from './components/prompt-cards';
import { RecentsRow } from './components/recents-row';
import { RecentlyLaunched } from './components/recently-launched';
import { SectionLabel } from './components/section-label';
import { WelcomeBanner } from './components/welcome-banner';
import { useRecents, useRecentlyShipped } from './hooks';
import * as styles from './index.css';

export function HomePage() {
  const { hello, tagline } = useGreeting();
  const workspaceId = useWorkspaceIdFromCurrentUrl() ?? 0;
  const { track } = useAnalytics();
  const { hasPermission } = usePermissions();
  const canViewAuditLogs = hasPermission('account/edit');
  const canInviteTeammates = hasPermission('account/team_members/edit');
  const isSidebarOpen = useSidebarActive('copilot');
  const { data: recents, isLoading: recentsLoading } = useRecents(workspaceId);
  const { data: shipped, isLoading: shippedLoading } = useRecentlyShipped(workspaceId);

  const hasTeamActivity = shippedLoading || (shipped != null && shipped.length > 0);

  const handleSubmit = useCallback(() => {
    track('home', 'agentInputSubmitted', {});
    openSidebar('copilot');
  }, [track]);

  return (
    <>
      <PageTitle title="Home" />
      <Page isFullScreen w="full">
        <PageHeader className={styles.pageHeader}>
          <PageHeaderTitle>{hello}</PageHeaderTitle>
          <PageHeaderDescription>{tagline}</PageHeaderDescription>
        </PageHeader>
        <PageBody className={styles.pageBody}>
          <Grid columns="minmax(0, 720px) 320px" gap="400" alignItems="flex-start" justifyContent="center">
            <div className={styles.leftColumn}>
              <Box id="in-app-target_dashboard" />
              <Flex direction="column" gap="300" minW="0">
                <WelcomeBanner />
                <Box w="full">
                  <Box mb="200">
                    <SectionLabel>Ready to keep going?</SectionLabel>
                  </Box>
                  <RecentsRow recents={recents} isLoading={recentsLoading} workspaceId={workspaceId} />
                </Box>
                <ActivityChart workspaceId={workspaceId} />
                <PromptCards workspaceId={workspaceId} />
              </Flex>
              <div
                className={clsx(styles.stickyFooter, isSidebarOpen && styles.stickyFooterHidden)}
                inert={isSidebarOpen || undefined}
              >
                <div className={styles.inputContainer}>
                  <CopilotInput onSubmit={handleSubmit} />
                </div>
              </div>
              <div className={styles.legacyDashboardFooter}>
                <LegacyDashboard />
              </div>
            </div>
            <Flex direction="column" gap="300" minW="0" overflow="hidden">
              <Box w="full">
                <Flex alignItems="center" justifyContent="space-between" mb="200">
                  <SectionLabel>What's your team up to?</SectionLabel>
                  {canViewAuditLogs ? (
                    <Text size="xs">
                      <Link
                        href={generatePath('/workspaces/:workspaceId/settings/audit-logs', { workspaceId })}
                        variant="primary"
                        icon="chevron-right"
                        iconPosition="trailing"
                      >
                        Audit logs
                      </Link>
                    </Text>
                  ) : null}
                </Flex>
                {hasTeamActivity ? (
                  <>
                    <RecentlyLaunched shipped={shipped} isLoading={shippedLoading} workspaceId={workspaceId} />
                    {canInviteTeammates ? (
                      <PlainLink
                        href="/settings/team/add"
                        className={styles.inviteTeammatesLink}
                        onClick={() => track('home', 'inviteTeammateClicked', {})}
                      >
                        <Avatar name="+" />
                        <Text size="s" color="subtle">
                          Invite teammates
                        </Text>
                      </PlainLink>
                    ) : null}
                  </>
                ) : (
                  <Flex direction="column" gap="200" alignItems="center" py="400">
                    <Icon name="manage-team" size="lg" color="subtle" />
                    <Text size="s" color="subtle" textAlign="center">
                      {canInviteTeammates ? 'See what your team is working on' : 'No recent activity'}
                    </Text>
                    {canInviteTeammates ? (
                      <Box>
                        <Button
                          variant="tertiary"
                          size="sm"
                          href="/settings/team/add"
                          onClick={() => track('home', 'inviteTeammateClicked', {})}
                        >
                          Invite teammates
                        </Button>
                      </Box>
                    ) : null}
                  </Flex>
                )}
              </Box>
              <MessageInvadersCard />
            </Flex>
          </Grid>
        </PageBody>
      </Page>
    </>
  );
}
